import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ChamadoToastComponent } from '../components/chamado-toast/chamado-toast.component';
import { ChamadoAvisoEvento } from '../interfaces/notificacao/notificacao-usuario.interface';
import { NotificacaoAPIService } from './notificacao-api.service';
import { UsuarioService } from './usuario.service';
import { WebSocketService } from './websocket.service';

/**
 * Avisos de chamado em tempo real (`ReceiveChamado`, só para os participantes): toast azul, que só
 * fecha no clique ou no X, e o sinal para o sino do header e a tela do chamado aberta se
 * atualizarem. O header chama {@link iniciar} ao montar (depois de cada login).
 */
@Injectable({
  providedIn: 'root'
})
export class ChamadoAvisoService {
  private atualizacoes$ = new Subject<ChamadoAvisoEvento>();
  private leituras$ = new Subject<void>();
  private assinatura: Subscription | null = null;

  constructor(
    private ws: WebSocketService,
    private toastr: ToastrService,
    private router: Router,
    private usuarioService: UsuarioService,
    private notificacaoApi: NotificacaoAPIService
  ) { }

  iniciar(): void {
    if (!this.assinatura) {
      this.assinatura = this.ws.getChamados().subscribe(aviso => this.aoReceber(aviso));
    }
  }

  /** Cada aviso recebido (o sino recarrega o contador; a tela do chamado, os dados). */
  getAtualizacoes(): Observable<ChamadoAvisoEvento> {
    return this.atualizacoes$.asObservable();
  }

  /** Alguma notificação de chamado foi lida fora do sino (o chamado foi aberto): o contador muda. */
  getLeituras(): Observable<void> {
    return this.leituras$.asObservable();
  }

  /** A tela do chamado avisa que abriu: o GET dele já marcou as notificações como lidas. */
  avisarLeitura(): void {
    this.leituras$.next();
  }

  /**
   * Abre o chamado na tela de edição (onde ficam as ações) quando o usuário tem o menu dela; senão,
   * na de visualização. As notificações dele são marcadas como lidas pelo próprio GET do chamado.
   */
  abrirChamado(chamadoId: number): void {
    const editar = `/chamado/editar/${chamadoId}`;
    const destino = this.usuarioService.temAcessoRota(editar) ? editar : `/chamado/visualizar/${chamadoId}`;
    this.router.navigateByUrl(destino);
  }

  private aoReceber(aviso: ChamadoAvisoEvento): void {
    // Rede de segurança: sem sessão, nenhum aviso chega à tela (nem à de login).
    if (!localStorage.getItem('token') || !aviso?.chamadoId) return;

    this.atualizacoes$.next(aviso);
    this.mostrarToast(aviso);
  }

  private mostrarToast(aviso: ChamadoAvisoEvento): void {
    // show(message, title): a frase do acontecimento em destaque, o complemento em cima. Sem
    // enableHtml: os dois são texto livre.
    const toast = this.toastr.show(aviso.titulo, aviso.mensagem, {
      toastComponent: ChamadoToastComponent,
      toastClass: 'ngx-toastr toast-chamado',
      disableTimeOut: true,
      closeButton: true,
      tapToDismiss: true
    });

    toast?.onTap.pipe(take(1)).subscribe(() => {
      // Marca antes de navegar: se o usuário só tem a lista, sem abrir o chamado, o sino já desconta.
      this.notificacaoApi.marcarLidasDaOrigem('chamado', aviso.chamadoId).subscribe({
        next: () => this.leituras$.next(),
        error: () => { }
      });
      this.abrirChamado(aviso.chamadoId);
    });
  }
}
